import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { api } from '../App';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { 
  Users, DollarSign, Tag, BarChart3, ArrowLeft, 
  TrendingUp, Clock, UserX, CheckCircle, ClockIcon, BookOpen, Megaphone, CreditCard, Brain 
} from 'lucide-react';
import { toast } from 'sonner';
import UsersTab from '../components/admin/UsersTab';
import PaymentsTab from '../components/admin/PaymentsTab';
import PendingPaymentsTab from '../components/admin/PendingPaymentsTab';
import AnalyticsTab from '../components/admin/AnalyticsTab';
import ReferralsTab from '../components/admin/ReferralsTab';
import ExtensionRequestsTab from '../components/admin/ExtensionRequestsTab';
import HowToTab from '../components/admin/HowToTab';
import AnnouncementsTab from '../components/admin/AnnouncementsTab';
import AIKnowledgeTab from '../components/admin/AIKnowledgeTab';

export default function AdminDashboard() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Check if user is admin
    if (user && user.role !== 'admin') {
      toast.error('Admin access required');
      navigate('/library');
      return;
    }
    
    fetchAnalytics();
  }, [user, navigate]);

  const fetchAnalytics = async () => {
    try {
      const response = await api.get('/admin/analytics');
      setAnalytics(response.data);
    } catch (error) {
      toast.error('Failed to load analytics');
      if (error.response?.status === 403) {
        navigate('/library');
      }
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-emerald-600">Loading admin dashboard...</div>
      </div>
    );
  }

  return (
    <div className="light" style={{ colorScheme: 'light' }}>
      <style>{`
        .light * { color-scheme: light !important; }
        .light .dark\\:bg-slate-800 { background-color: white !important; }
        .light .dark\\:bg-slate-700 { background-color: white !important; }
        .light .dark\\:text-slate-100 { color: #111827 !important; }
        .light .dark\\:text-slate-200 { color: #1f2937 !important; }
        .light .dark\\:text-slate-300 { color: #374151 !important; }
        .light .dark\\:text-slate-400 { color: #6b7280 !important; }
        .light .dark\\:border-slate-700 { border-color: #e5e7eb !important; }
      `}</style>
    <div className="min-h-screen bg-gradient-to-br from-emerald-50 via-white to-blue-50">
      {/* Header */}
      <div className="bg-white border-b shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3 sm:py-4">
          <div className="flex items-center justify-between gap-2">
            <div className="flex items-center gap-2 sm:gap-4 flex-1 min-w-0">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => navigate('/library')}
                className="flex items-center gap-1 sm:gap-2 shrink-0"
              >
                <ArrowLeft className="w-4 h-4" />
                <span className="hidden sm:inline">Back to App</span>
                <span className="sm:hidden">Back</span>
              </Button>
              <div className="min-w-0">
                <h1 className="text-lg sm:text-2xl font-bold text-gray-900 truncate">Admin Dashboard</h1>
                <p className="text-xs sm:text-sm text-gray-500 hidden sm:block">LexProcure PH Management</p>
              </div>
            </div>
            <div className="text-right hidden md:block">
              <p className="text-sm font-medium text-gray-900">{user?.full_name}</p>
              <p className="text-xs text-gray-500">{user?.email}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Analytics Overview Cards */}
      {analytics && (
        <div className="max-w-7xl mx-auto px-2 sm:px-4 lg:px-8 py-4 sm:py-6">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 mb-4 sm:mb-6">
            <Card className="bg-white">
              <CardHeader className="pb-2 p-3 sm:p-6 sm:pb-2">
                <CardTitle className="text-xs sm:text-sm font-medium text-gray-600">Total Users</CardTitle>
              </CardHeader>
              <CardContent className="p-3 pt-0 sm:p-6 sm:pt-0">
                <div className="flex items-center justify-between">
                  <p className="text-2xl sm:text-3xl font-bold text-gray-900">{analytics.total_users}</p>
                  <Users className="w-6 h-6 sm:w-8 sm:h-8 text-blue-500" />
                </div>
                <p className="text-xs text-gray-500 mt-1">
                  +{analytics.recent_registrations} this month
                </p>
              </CardContent>
            </Card>

            <Card className="bg-white">
              <CardHeader className="pb-2 p-3 sm:p-6 sm:pb-2">
                <CardTitle className="text-xs sm:text-sm font-medium text-gray-600">Active Subscribers</CardTitle>
              </CardHeader>
              <CardContent className="p-3 pt-0 sm:p-6 sm:pt-0">
                <div className="flex items-center justify-between">
                  <p className="text-2xl sm:text-3xl font-bold text-emerald-600">{analytics.active_users}</p>
                  <CheckCircle className="w-6 h-6 sm:w-8 sm:h-8 text-emerald-500" />
                </div>
                <p className="text-xs text-gray-500 mt-1">
                  {analytics.conversion_rate}% conversion
                </p>
              </CardContent>
            </Card>

            <Card className="bg-white">
              <CardHeader className="pb-2 p-3 sm:p-6 sm:pb-2">
                <CardTitle className="text-xs sm:text-sm font-medium text-gray-600">Trial Users</CardTitle>
              </CardHeader>
              <CardContent className="p-3 pt-0 sm:p-6 sm:pt-0">
                <div className="flex items-center justify-between">
                  <p className="text-2xl sm:text-3xl font-bold text-blue-600">{analytics.trial_users}</p>
                  <Clock className="w-6 h-6 sm:w-8 sm:h-8 text-blue-500" />
                </div>
                <p className="text-xs text-gray-500 mt-1">
                  {analytics.upcoming_expirations} expiring
                </p>
              </CardContent>
            </Card>

            <Card className="bg-white">
              <CardHeader className="pb-2 p-3 sm:p-6 sm:pb-2">
                <CardTitle className="text-xs sm:text-sm font-medium text-gray-600">Total Revenue</CardTitle>
              </CardHeader>
              <CardContent className="p-3 pt-0 sm:p-6 sm:pt-0">
                <div className="flex items-center justify-between">
                  <p className="text-xl sm:text-3xl font-bold text-emerald-600">₱{analytics.total_revenue.toLocaleString()}</p>
                  <TrendingUp className="w-6 h-6 sm:w-8 sm:h-8 text-emerald-500" />
                </div>
                <p className="text-xs text-gray-500 mt-1">
                  Manual payments
                </p>
              </CardContent>
            </Card>
          </div>

          {/* Main Tabs */}
          <Tabs defaultValue="users" className="w-full">
            <div className="overflow-x-auto -mx-2 px-2 sm:mx-0 sm:px-0">
              <TabsList className="inline-flex w-max min-w-full lg:w-auto mb-2">
                <TabsTrigger value="users" className="flex items-center gap-1 text-xs sm:text-sm px-2 sm:px-4">
                  <Users className="w-3 h-3 sm:w-4 sm:h-4" />
                  <span className="hidden sm:inline">Users</span>
                  <span className="sm:hidden">Users</span>
                </TabsTrigger>
                <TabsTrigger value="extensions" className="flex items-center gap-1 text-xs sm:text-sm px-2 sm:px-4">
                  <Clock className="w-3 h-3 sm:w-4 sm:h-4" />
                  <span className="hidden sm:inline">Extensions</span>
                  <span className="sm:hidden">Ext</span>
                </TabsTrigger>
                <TabsTrigger value="referrals" className="flex items-center gap-1 text-xs sm:text-sm px-2 sm:px-4">
                  <TrendingUp className="w-3 h-3 sm:w-4 sm:h-4" />
                  <span className="hidden sm:inline">Referrals</span>
                  <span className="sm:hidden">Refs</span>
                </TabsTrigger>
                <TabsTrigger value="payments" className="flex items-center gap-1 text-xs sm:text-sm px-2 sm:px-4">
                  <DollarSign className="w-3 h-3 sm:w-4 sm:h-4" />
                  <span className="hidden sm:inline">Pay Record</span>
                  <span className="sm:hidden">Pay</span>
                </TabsTrigger>
                <TabsTrigger value="pending-payments" className="flex items-center gap-1 text-xs sm:text-sm px-2 sm:px-4">
                  <CreditCard className="w-3 h-3 sm:w-4 sm:h-4" />
                  <span className="hidden sm:inline">Payment Orders</span>
                  <span className="sm:hidden">Orders</span>
                </TabsTrigger>
                <TabsTrigger value="analytics" className="flex items-center gap-1 text-xs sm:text-sm px-2 sm:px-4">
                  <BarChart3 className="w-3 h-3 sm:w-4 sm:h-4" />
                  <span className="hidden sm:inline">Analytics</span>
                  <span className="sm:hidden">Stats</span>
                </TabsTrigger>
                <TabsTrigger value="announcements" className="flex items-center gap-1 text-xs sm:text-sm px-2 sm:px-4">
                  <Megaphone className="w-3 h-3 sm:w-4 sm:h-4" />
                  <span className="hidden sm:inline">Announce</span>
                  <span className="sm:hidden">News</span>
                </TabsTrigger>
                <TabsTrigger value="howto" className="flex items-center gap-1 text-xs sm:text-sm px-2 sm:px-4">
                  <BookOpen className="w-3 h-3 sm:w-4 sm:h-4" />
                  <span className="hidden sm:inline">How To</span>
                  <span className="sm:hidden">Help</span>
                </TabsTrigger>
                <TabsTrigger value="ai-knowledge" className="flex items-center gap-1 text-xs sm:text-sm px-2 sm:px-4">
                  <Brain className="w-3 h-3 sm:w-4 sm:h-4" />
                  <span className="hidden sm:inline">AI Knowledge</span>
                  <span className="sm:hidden">AI</span>
                </TabsTrigger>
              </TabsList>
            </div>

            <TabsContent value="users" className="mt-6">
              <UsersTab onUpdate={fetchAnalytics} />
            </TabsContent>

            <TabsContent value="extensions" className="mt-6">
              <ExtensionRequestsTab />
            </TabsContent>

            <TabsContent value="referrals" className="mt-6">
              <ReferralsTab />
            </TabsContent>

            <TabsContent value="payments" className="mt-6">
              <PaymentsTab onUpdate={fetchAnalytics} />
            </TabsContent>

            <TabsContent value="pending-payments" className="mt-6">
              <PendingPaymentsTab onUpdate={fetchAnalytics} />
            </TabsContent>

            <TabsContent value="analytics" className="mt-6">
              <AnalyticsTab analytics={analytics} />
            </TabsContent>

            <TabsContent value="announcements" className="mt-6">
              <AnnouncementsTab />
            </TabsContent>

            <TabsContent value="howto" className="mt-6">
              <HowToTab />
            </TabsContent>

            <TabsContent value="ai-knowledge" className="mt-6">
              <AIKnowledgeTab />
            </TabsContent>
          </Tabs>
        </div>
      )}
    </div>
    </div>
  );
}
